import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Autocomplete,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { nawyTheme as theme } from "@/styles/nawy-theme/nawy-theme";
import { DeveloperTrackItem } from "@/modules/common/types";
import { getDeveloperUsers } from "../api"; // Adjust import path if needed
import { getDevelopers } from "@/modules/leads/api/index.v2";
import { queryCommonFlags } from "@/api/constants";

interface CreateTrackRowProps {
  track: DeveloperTrackItem;
  index: number;
  onDelete: (id: string) => void;
  onChange: (id: string, developerUId: string, developerUserUId: string) => void;
  canDelete: boolean;
  errors?: {
    developer?: boolean;
    user?: boolean;
  };
}

const CreateTrackRow: React.FC<CreateTrackRowProps> = ({
  track,
  index,
  onDelete,
  onChange,
  canDelete,
  errors,
}) => {
  const [searchUsers, setSearchUsers] = useState("");
  const [searchDevelopers, setSearchDevelopers] = useState("");

  // Fetch Developers locally for this row
  const { data: developersData, isLoading: isLoadingDevelopers } = useQuery({
    queryKey: ["developers", searchDevelopers],
    queryFn: () =>
      getDevelopers(searchDevelopers ? { name: searchDevelopers } : {}),
    ...queryCommonFlags,
  });

  const developers = developersData?.data?.results || [];

  // Fetch users for this specific row's chosen developer
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["developer-users", track.developerUId, searchUsers],
    queryFn: () =>
      getDeveloperUsers({
        developerUId: track.developerUId,
        search: searchUsers,
        excludeBlocked: true,
      }),
    enabled: !!track.developerUId,
    ...queryCommonFlags,
  });

  const users = usersData?.data?.results || [];

  const handleDeveloperSelect = (newValue: any) => {
    // If developer changes, we clear the user
    onChange(
      track.id,
      newValue?.uid || "",
      "" // Reset user
    );
    setSearchUsers("");
  };

  const handleUserSelect = (newValue: any) => {
    onChange(
      track.id,
      track.developerUId,
      newValue?.uid || ""
    );
  };

  const selectedDeveloper = useMemo(() => 
    developers.find((d: any) => d.uid === track.developerUId) || null, 
  [developers, track.developerUId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          Track {index + 1}
        </Typography>
        {canDelete && (
          <IconButton onClick={() => onDelete(track.id)} size="small">
            <Trash2 size={18} color={theme.nawy.colors.semantic.text.secondary} />
          </IconButton>
        )}
      </Box>

      {/* Developer Selector */}
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: theme.nawy.colors.semantic.text.secondary,
            marginBottom: theme.nawy.spacing.xxs,
          }}
        >
          Developer
        </Typography>
        <FormControl sx={{ width: "100%" }}>
          <Autocomplete
            value={selectedDeveloper}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') return;
              handleDeveloperSelect(newValue);
            }}
            onInputChange={(_, newInputValue) => {
              setSearchDevelopers(newInputValue);
            }}
            options={developers}
            loading={isLoadingDevelopers}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select developer"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    ...(errors?.developer && {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d32f2f",
                      },
                    }),
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingDevelopers ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.uid === value?.uid}
            sx={{ width: "100%" }}
          />
        </FormControl>
      </Box>

      {/* User (Agent) Selector */}
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: theme.nawy.colors.semantic.text.secondary,
            marginBottom: theme.nawy.spacing.xxs,
          }}
        >
          Agent
        </Typography>
        <FormControl sx={{ width: "100%" }}>
          <Autocomplete
            value={users.find(u => u.uid === track.developerUserUId) || null}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') return;
              handleUserSelect(newValue);
            }}
            onInputChange={(_, newInputValue) => {
              setSearchUsers(newInputValue);
            }}
            options={users} 
            loading={isLoadingUsers}
            disabled={!track.developerUId}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={track.developerUId ? "Select agent (optional)" : "Select a developer first"}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "white",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingUsers && track.developerUId ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.uid === value?.uid}
            sx={{ width: "100%" }}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

export default CreateTrackRow;
